import {type MatchParams} from '@wandb/weave/common/types/base';
import {decodeURIComponentSafe} from '@wandb/weave/common/util/url';
import {type match, matchPath, useLocation} from 'react-router';

import {
  ACCOUNT_SETTINGS_PATHS,
  USER_SETTINGS_PATH,
} from './account/accountSettingPaths';
import {
  ACADEMIC_APPLICATION_PATH,
  ONBOARDING_FLOW_PATH,
  STARTUP_PROGRAM_PATH,
  TRIAL_END_PATH,
} from './onboarding/onboardingPaths';
import {ORG_SETTINGS} from './organization/orgPaths';
import {
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
} from './report/reportPaths';
import {ROUTES} from './routes';
import {
  CREATE_TEAM_PATH,
  PROFILE_BASE_PATH,
  PROFILE_BASE_PROJECTS_PATH,
  PROFILE_BASE_REPORTS_PATH,
  PROFILE_BASE_SETTINGS_PATH,
  PROFILE_BASE_STARS_PATH,
  PROFILE_NOTEBOOKS_PATH,
  PROFILE_PATH,
  PROFILE_PROJECTS_PATH,
  PROFILE_REPORTS_PATH,
  PROFILE_STARS_PATH,
} from './team/teamPaths';
import {HISTORIC_USAGE_PATH, USAGE_PATH} from './usage/usagePaths';

export function useCurrentRouteMatchParams(): MatchParams {
  const currentRouteMatch = useCurrentRouteMatch();

  const params: MatchParams = currentRouteMatch?.params ?? {};
  const {projectName, artifactTypeName} = params;

  return {
    ...params,

    // HAX: the project name URI we get from react-router might be encoded.
    // Decode this URI component before using it.
    // If this happens again on another URI component (e.g. entity name), we
    // should do more general fix at the react-router layer.
    ...(projectName != null
      ? {projectName: decodeURIComponentSafe(projectName)}
      : {}),

    // If artifact type name is available, decode any '@' chars
    // for the correct artifact type name to display in the nav.
    ...(artifactTypeName != null
      ? {artifactTypeName: decodeURIComponentSafe(artifactTypeName)}
      : {}),
  };
}

export function useCurrentRouteMatch(): match | null {
  const location = useLocation();
  return getRouteMatch(location.pathname);
}

export function getRouteMatch(path: string): match | null {
  for (const r of ROUTES) {
    const m = matchPath(path, {path: r.path, exact: r.exact});
    if (m != null) {
      return m;
    }
  }
  return null;
}

export function isReportViewPath(path: string): boolean {
  return isSpecificPath(path, [
    REPORT_PAGE_VIEW_PATH,
    REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
  ]);
}

export function hasEntityNameRouteParameter(path: string): boolean {
  const m = getRouteMatch(path);
  // eslint-disable-next-line no-prototype-builtins
  return m?.params.hasOwnProperty('entityName') ?? false;
}

export function isUsagePath(path: string): boolean {
  return isSpecificPath(path, [USAGE_PATH]);
}

export function isHistoricUsagePath(path: string): boolean {
  return isSpecificPath(path, [HISTORIC_USAGE_PATH]);
}

export function isBillingPath(path: string): boolean {
  return isSpecificPath(path, [...ACCOUNT_SETTINGS_PATHS]);
}

export function isUserSettingsPath(path: string): boolean {
  return isSpecificPath(path, [USER_SETTINGS_PATH]);
}

export function isTrialEndPath(path: string): boolean {
  return isSpecificPath(path, [TRIAL_END_PATH]);
}

export function isAcademicApplicationPath(path: string): boolean {
  return isSpecificPath(path, [ACADEMIC_APPLICATION_PATH]);
}

export function isCreateTeamPath(path: string): boolean {
  return isSpecificPath(path, [CREATE_TEAM_PATH]);
}

export function isStartupProgramPath(path: string): boolean {
  return isSpecificPath(path, [STARTUP_PROGRAM_PATH]);
}

export function isSpecificPath(path: string, pathFormats: string[]): boolean {
  const m = getRouteMatch(path);
  if (m == null) {
    return false;
  }
  return pathFormats.indexOf(m.path) !== -1;
}

export function isProfilePath(path: string): boolean {
  const m = getRouteMatch(path);
  return (
    m?.path === PROFILE_PATH ||
    m?.path === PROFILE_PROJECTS_PATH ||
    m?.path === PROFILE_REPORTS_PATH ||
    m?.path === PROFILE_NOTEBOOKS_PATH ||
    m?.path === PROFILE_STARS_PATH ||
    isCanonicalProfilePath(path)
  );
}

/** /profile/:entityName routes only — not legacy /:entityName team pages. */
export function isCanonicalProfilePath(path: string): boolean {
  const m = getRouteMatch(path);
  return (
    m?.path === PROFILE_BASE_PATH ||
    m?.path === PROFILE_BASE_PROJECTS_PATH ||
    m?.path === PROFILE_BASE_REPORTS_PATH ||
    m?.path === PROFILE_BASE_STARS_PATH ||
    m?.path === PROFILE_BASE_SETTINGS_PATH
  );
}

export function isOnboardingFlowPath(path: string): boolean {
  const m = getRouteMatch(path);
  return m?.path === ONBOARDING_FLOW_PATH;
}

export function isOrgSettingsPath(): boolean {
  const path = location.pathname;
  return path.startsWith(ORG_SETTINGS);
}
